// po-approve-reject-page.component.ts

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PurchaseOrderService } from '../purchase-order.service';
import { PoRemarkComponent } from '../pending-purhcase-order/po-remark/po-remark.component';


@Component({
  selector: 'app-po-approve-reject-page',
  templateUrl: './po-approve-reject-page.component.html',
  styleUrls: ['./po-approve-reject-page.component.scss']
})
export class PoApproveRejectPageComponent implements OnInit {
  po_id: any;
  poData: any = {};
  rowData: any = [];
  tentativeData: any = [];
  poTotalAmount: number = 0;
  isLoading: boolean = true;
  isReadOnly: boolean = false;
  empId: any;
  companyId: any;
  poMilestoneData: any[] = [];
  isMilestoneRequired: boolean = false;
  approvalStatus: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private purchaseService: PurchaseOrderService,
    private _matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Get params from URL
    this.route.queryParams.subscribe(params => {
      this.po_id = params['poId'] || params['po_id'];
      this.empId = params['empId'] || params['employeeId'];
      this.companyId = params['companyId'] || params['company_id'];
      
      console.log('📋 PO ID:', this.po_id);
      console.log('📋 Emp ID:', this.empId);
      
      if (this.po_id) {
        this.getPODetails();
      } else {
        this.toastr.error('PO ID not found in URL');
        this.isLoading = false;
      }
    });

    // Try to get from localStorage if not in URL
    if (!this.empId) {
      try {
        const storedData = localStorage.getItem('user_details') ?? '{}';
        const userDetails = JSON.parse(storedData);
        if (userDetails && userDetails.employeeId) {
          this.empId = userDetails.employeeId;
          this.companyId = userDetails.companyId;
        }
      } catch (e) {}
    }
  }

  getPODetails() {
    const data = { po_id: this.po_id };
    
    this.purchaseService.getAllPurchaseorderDetails(data).subscribe({
      next: (res: any) => {
        if (res?.body?.data) {
          this.rowData = res.body.data?.poItemData || [];
          this.tentativeData = res.body.data?.addressData || [];
          this.poData = res.body.data?.poData || {};
          this.poMilestoneData = res.body.data?.poMilestoneData || [];
          this.isMilestoneRequired = this.poData?.isMilestoneRequired || false;
          
          if (!this.poData.currency_code) {
            this.poData.currency_code = res.body.data?.currency_code || 'INR';
          }
          
          this.totalItemAmount();
          this.checkAuthorization();
          this.isLoading = false;
        } else {
          this.toastr.error('PO not found');
          this.isLoading = false;
        }
      },
      error: (err: any) => {
        this.toastr.error('Failed to load PO details');
        this.isLoading = false;
        console.error('Error:', err);
      }
    });
  }

  checkAuthorization() {
    // Check if current user is the approver
    if (this.poData && this.poData.tbl_poWorkFlowApproves) {
      const currentApproval = this.poData.tbl_poWorkFlowApproves?.find(
        (item: any) => {
          const empIdMatch = String(item.emp_id) === String(this.empId);
          const statusMatch = item.progressStatus === 'Open' || item.progressStatus === 'Pending';
          return empIdMatch && statusMatch;
        }
      );
      
      if (!currentApproval && this.empId) {
        this.toastr.warning('You are not the current approver. View only mode.', '', {
          timeOut: 3000
        });
        this.isReadOnly = true;
      } else if (!this.empId) {
        this.isReadOnly = true;
        this.toastr.warning('Please login to approve/reject', '', { timeOut: 3000 });
      } else {
        console.log('✅ User is authorized to approve');
      }
    }
  }

  totalItemAmount() {
    this.poTotalAmount = 0;
    if (!Array.isArray(this.rowData)) this.rowData = [];

    this.rowData.forEach((item: any) => {
      const qty = Number(item?.quantity) || 0;
      const itemPrice = Number(item?.itemPrice) || 0;
      const priceIncluding = Number(item?.priceIncludingCharges);
      const total = !isNaN(priceIncluding) && priceIncluding > 0
        ? priceIncluding
        : itemPrice * qty;
      this.poTotalAmount += total;
    });
  }

  // ✅ Open Remark Popup on Approve/Reject
  openRemarkPopup(status: string) {
    if (this.isReadOnly) {
      this.toastr.warning('You are not authorized to perform this action');
      return;
    }

    if (!this.empId) {
      this.toastr.error('Employee ID not found. Please login again.');
      return;
    }

    this.approvalStatus = status;
    
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.height = 'auto';
    dialogConfig.width = '400px';
    dialogConfig.maxWidth = '100%';
    dialogConfig.data = { 
      po_id: this.po_id, 
      status: status,
      emp_id: this.empId,
      isPublic: true,
      poCode: this.poData?.poCode || ''
    };
    
    const dialogRef = this._matDialog.open(PoRemarkComponent, dialogConfig);
    
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        this.toastr.success(result.message || 'Action completed successfully');
        // Redirect to thank you page
        setTimeout(() => {
          this.router.navigate(['/po-status'], { 
            queryParams: { 
              status: status.toLowerCase(),
              poCode: this.poData?.poCode || ''
            }
          });
        }, 1500);
      }
    });
  }

  stripHtmlTags(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  downloadPDF() {
    // Implement PDF download if needed
    this.toastr.info('PDF download feature coming soon');
  }

  getStatusBadge(status: string): string {
    switch(status) {
      case 'Pending': return 'bg-warning';
      case 'Approved': return 'bg-success';
      case 'Rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  goBack() {
    this.router.navigate(['/admin/purchase-order/pending-purchase-order']);
  }
}